import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import {
  DeleteMedicineGroup,
  GetMedicineGroups
} from "../../../../api/medicalStore/master/medicineGroupAPI";

const ListMedicineGroup = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* ================= FETCH DATA ================= */
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetMedicineGroups();

      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          name: item.name || "",
          discountPercentage: item.discountPercentage || 0,
          createdAt: new Date(item.createdAt).toLocaleDateString()
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ================= SEARCH ================= */
  useEffect(
    () => {
      const results = data.filter(item =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
      setFilteredData(results);
    },
    [search, data]
  );

  /* ================= ACTIONS ================= */
  const handleEditClick = id => {
    navigate(`/medical-store/master/medicine-group/edit/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will soft delete the medicine group!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteMedicineGroup(id);
          if (response.success) {
            showToast("success", response.message);
            setData(data.filter(item => item.id !== id));
            setFilteredData(filteredData.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    {
      name: "S.No",
      selector: row => row.index,
      width: "10%",
      sortable: true
    },
    {
      name: "Group Name",
      selector: row => row.name,
      sortable: true
    },
    {
      name: "Discount (%)",
      selector: row => `${row.discountPercentage}%`,
      sortable: true
    },
    {
      name: "Created At",
      selector: row => row.createdAt,
      sortable: true
    },
    {
      name: "Actions",
      cell: row =>
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>,
      ignoreRowClick: true,
      button: true,
      width: "20%"
    }
  ];

  /* ================= CSV ================= */
  const headers = [
    { label: "S.No", key: "index" },
    { label: "Group Name", key: "name" },
    { label: "Discount Percentage", key: "discountPercentage" },
    { label: "Created At", key: "createdAt" }
  ];

  const ExportCSV = ({ data }) =>
    <CSVLink data={data} headers={headers} filename="medicine-groups.csv">
      <button className="btn btn-primary" id="export">
        Export
      </button>
    </CSVLink>;

  /* ================= UI ================= */
  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="Medicine Groups" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>Medicine Group List</h5>
              <Link
                to={`${process.env
                  .PUBLIC_URL}/medical-store/master/medicine-group/add`}
              >
                <button className="btn btn-primary">
                  Create Medicine Group
                </button>
              </Link>
            </div>

            <div className="row mt-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search Medicine Group..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListMedicineGroup;
